/**
 * Admin Configuration for Halal Auto Pricing
 * 
 * These values control the automated pricing calculation for car listings.
 * Update these values as needed to adjust pricing parameters across the platform.
 */

export interface PricingConfig {
  /** Annual Percentage Rate (APR) as a percentage (e.g., 5.5 for 5.5%) */
  apr: number;
  
  /** Markup cap as a decimal (e.g., 0.30 for 30% markup) */
  markupCap: number;
  
  /** Source Score - APR Basis Credit Score (the base credit score used for APR calculation) */
  sourceScore: number;
  
  /** Safety Margin - Additional points added to source score for minimum credit score requirement */
  safetyMargin: number;
}

/**
 * Default pricing configuration
 * These values can be updated by administrators as needed
 */
const defaultConfig: PricingConfig = {
  apr: 5.5,           // 5.5% APR
  markupCap: 0.30,    // 30% markup cap
  sourceScore: 650,   // Base credit score
  safetyMargin: 50    // 50 point safety margin
};

/**
 * Get pricing configuration from API, with fallback to localStorage and defaults
 */
export const getPricingConfig = async (): Promise<PricingConfig> => {
  // Try to fetch from API first
  if (typeof window !== 'undefined') {
    try {
      const { apiService } = await import('../services/api');
      const config = await apiService.getPricingConfig();
      // Cache in localStorage for offline use
      localStorage.setItem('pricingConfig', JSON.stringify(config));
      return config;
    } catch (error) {
      console.warn('Failed to fetch pricing config from API, trying localStorage:', error);
      
      // Fallback to localStorage
      const savedConfig = localStorage.getItem('pricingConfig');
      if (savedConfig) {
        try {
          const parsed = JSON.parse(savedConfig);
          // Validate the parsed config has all required fields
          if (
            typeof parsed.apr === 'number' &&
            typeof parsed.markupCap === 'number' &&
            typeof parsed.sourceScore === 'number' &&
            typeof parsed.safetyMargin === 'number'
          ) {
            return parsed;
          }
        } catch (parseError) {
          console.warn('Error parsing saved pricing config, using defaults:', parseError);
        }
      }
    }
  }
  return defaultConfig;
};

/**
 * Synchronous version that uses cached localStorage value (for immediate use)
 * Use getPricingConfig() for fresh data from API
 */
export const getCachedPricingConfig = (): PricingConfig => {
  if (typeof window !== 'undefined') {
    const savedConfig = localStorage.getItem('pricingConfig');
    if (savedConfig) {
      try {
        const parsed = JSON.parse(savedConfig);
        if (
          typeof parsed.apr === 'number' &&
          typeof parsed.markupCap === 'number' &&
          typeof parsed.sourceScore === 'number' &&
          typeof parsed.safetyMargin === 'number'
        ) {
          return parsed;
        }
      } catch (error) {
        console.warn('Error parsing saved pricing config, using defaults:', error);
      }
    }
  }
  return defaultConfig;
};

/**
 * Export default pricing config (for synchronous access)
 * Use getPricingConfig() for async API fetch
 */
export const pricingConfig: PricingConfig = defaultConfig;

